import moment from "moment";
import {
  AVAILABILITY_LOG_TYPE,
  BASIC_HEIGHT_UNIT,
  EARLIEST_TIME,
  HOURS_PER_DAY,
  LOCK_TYPES,
  ONE_HOUR,
  TIMELINE_WIDTH,
  TIME_UNITS,
} from "@miplatform/constants";
import { getDate, isSameDay } from "../date/date";

export const getTimeIntervals = (increment) => {
  const formatString = increment < 30 ? "H:mm" : "h:mm A";
  const startTime = moment(EARLIEST_TIME, formatString);
  const timesArray = [];
  for (
    let i = 0;
    i < ((HOURS_PER_DAY - EARLIEST_TIME) * ONE_HOUR) / increment;
    i++
  ) {
    timesArray.push({
      id: i + 1,
      time: startTime.format(formatString),
    });
    startTime.add(increment, "minutes");
  }
  return timesArray;
};

export const getAdjustedHeight = (increment, zoom = 1) =>
  increment * BASIC_HEIGHT_UNIT * zoom;

export const isWithinLockPermission = ({ event, updates }) => {
  if (
    (event.lockTypes.includes(LOCK_TYPES.DATE) &&
      !isSameDay(event.scheduledStartDate, updates.scheduledStartDate)) ||
    (event.lockTypes.includes(LOCK_TYPES.USER) &&
      event.assignedUserId !== updates.userId) ||
    (event.lockTypes.includes(LOCK_TYPES.TIME) &&
      event.scheduledStartDate !== updates.scheduledStartDate &&
      event.scheduledEndDate !== updates.scheduledEndDate)
  ) {
    return false;
  }

  return true;
};

const restrictToBoundingRect = (transform, rect, boundingRect = {}) => {
  const value = {
    ...transform,
  };

  const { top, left, height, width } = boundingRect;

  if (rect.top + transform.y <= top) {
    value.y = top - rect.top;
  } else if (rect.bottom + transform.y >= top + height) {
    value.y = top + height - rect.bottom;
  }

  if (rect.left + transform.x <= left) {
    value.x = left - rect.left;
  } else if (rect.right + transform.x >= left + width) {
    value.x = left + width - rect.right;
  }

  return value;
};

export const dndBoundingModifier =
  (offset = 0) =>
  ({ draggingNodeRect, transform, scrollableAncestorRects, windowRect }) => {
    const [firstAncestor, secondAncestor] = scrollableAncestorRects;

    if (!draggingNodeRect || !firstAncestor || !secondAncestor || !windowRect) {
      return transform;
    }

    const isInBucket = firstAncestor.left !== secondAncestor.left;

    const boundingRect = isInBucket
      ? {
          top: secondAncestor.top,
          left: secondAncestor.left + TIMELINE_WIDTH,
          height: secondAncestor.height,
          width: secondAncestor.width,
        }
      : {
          top: firstAncestor.top + offset,
          left: firstAncestor.left + TIMELINE_WIDTH,
          height: windowRect.bottom - firstAncestor.top - offset,
          width: firstAncestor.width - TIMELINE_WIDTH,
        };

    return restrictToBoundingRect(transform, draggingNodeRect, boundingRect);
  };

export const isFullDayAvailabilityLog = (log) => {
  if (!log.from || !log.to) return false;

  // Parse the log times preserving timezone info
  const logFrom = moment(log.from);
  const logTo = moment(log.to);

  // Check if log starts at midnight (00:00:00) in its own timezone
  const startsAtMidnight =
    logFrom.hour() === 0 && logFrom.minute() === 0 && logFrom.second() === 0;

  // Check if log ends at midnight of next day (exclusive end) in its own timezone
  // This means it covers a full 24-hour period
  const expectedEnd = logFrom.clone().endOf("day");
  const endsAtMidnightToday =
    logTo.hour() === 23 &&
    logTo.minute() === 59 &&
    logTo.second() === 59 &&
    logTo.isSame(expectedEnd, "day");

  // Check duration is exactly 24 hours (1440 minutes)
  const durationMinutes = logTo.diff(logFrom, "seconds");
  const is24Hours = durationMinutes === 86399; // 24 hours in seconds minus 1 second for inclusive start and exclusive end
  return startsAtMidnight && endsAtMidnightToday && is24Hours;
};

export const getDeactivatedIntervals = (availabilityLog, timeSlots, date) => {
  if (!availabilityLog || availabilityLog.length === 0) {
    return {};
  }

  const intervals = {};

  availabilityLog.forEach((log) => {
    if (
      log.type === AVAILABILITY_LOG_TYPE.INACTIVE &&
      isSameDay(log.from, date)
    ) {
      const from = getDate(log.from);
      const to = getDate(log.to);
      const durationMinutes = to.diff(from, TIME_UNITS.MINUTE);
      const startHour = from.hour();
      const startMinute = from.minutes();

      let currentHour = startHour;
      let remainingMinutes = durationMinutes;
      let currentStartMinute = startMinute;

      while (remainingMinutes > 0) {
        const timeSlot = findTimeSlotByHour(timeSlots, currentHour);
        if (timeSlot) {
          const minutesInThisHour = Math.min(
            60 - currentStartMinute,
            remainingMinutes
          );

          intervals[timeSlot.id] = {
            slots: intervals[timeSlot.id]?.slots || [],
            note: log.note,
          };

          if (minutesInThisHour >= 60 && currentStartMinute === 0) {
            intervals[timeSlot.id].slots = [0, 1, 2, 3];
          } else {
            const affectedIntervals = calculateIntervalsForSameHour(
              currentStartMinute,
              currentStartMinute + minutesInThisHour
            );

            if (intervals[timeSlot.id].slots) {
              intervals[timeSlot.id].slots = [
                ...new Set([
                  ...intervals[timeSlot.id].slots,
                  ...affectedIntervals,
                ]),
              ];
            } else {
              intervals[timeSlot.id].slots = affectedIntervals;
            }
          }

          remainingMinutes -= minutesInThisHour;
          currentHour += 1;
          currentStartMinute = 0;
        } else {
          break;
        }
      }
    }
  });

  return intervals;
};

export const findTimeSlotByHour = (timeSlots, hour) => {
  const normalizedHour = hour % 24;
  const hour12 = normalizedHour % 12 === 0 ? 12 : normalizedHour % 12;
  const period = normalizedHour < 12 || normalizedHour === 24 ? "AM" : "PM";
  const timeString = `${hour12}:00 ${period}`;

  return timeSlots.find((slot) => slot.time === timeString);
};

export const calculateIntervalsForSameHour = (startMinutes, endMinutes) => {
  const intervals = [];
  const intervalBoundaries = [0, 15, 30, 45, 60];

  for (let i = 0; i < 4; i++) {
    const intervalStart = intervalBoundaries[i];
    const intervalEnd = intervalBoundaries[i + 1];

    const overlapStart = Math.max(intervalStart, startMinutes);
    const overlapEnd = Math.min(intervalEnd, endMinutes);
    const overlapDuration = overlapEnd - overlapStart;

    if (overlapDuration > 1) {
      intervals.push(i);
    }
  }

  return intervals;
};
